import { ObjectiveEditor, type Objective } from "./Objectives";
import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { api, type Test, type CampaignCase, type SavedTrafficProfile } from "../../api/client";
import { Field, Modal } from "../../components";
import { duration } from "./report";

/** How long a run has been going, ticking every second. */
export function Elapsed({ since }: { since: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return <>{duration(Math.max(0, (now - Date.parse(since)) / 1000))}</>;
}

/** The realm page lives at /lab-scenarios/:id for everyone; old links land there. */
export default function CampaignPage() {
  const { id = "" } = useParams();
  return <Navigate to={`/lab-scenarios/${id}`} replace />;
}

export function CaseForm({ campaignID, testCase, agents, mixes, tests, onClose, onSaved }: {
  campaignID: string;
  testCase?: CampaignCase;
  /** The campaign's agents: the only ones a case's traffic may run between. */
  agents: { id: string; name: string }[];
  mixes: SavedTrafficProfile[];
  tests: Test[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(testCase?.name ?? "");
  const [description, setDescription] = useState(testCase?.description ?? "");
  const [objectives, setObjectives] = useState<Objective[]>(testCase?.objectives ?? []);
  const [withTraffic, setWithTraffic] = useState(!!testCase?.traffic);
  const [mix, setMix] = useState(testCase?.traffic?.profile_id ?? mixes[0]?.id ?? "");
  const [client, setClient] = useState(testCase?.traffic?.client_agent_id ?? agents[0]?.id ?? "");
  const [responder, setResponder] = useState(testCase?.traffic?.responder_agent_id ?? agents[1]?.id ?? "");
  const trafficReady = !withTraffic || (!!mix && !!client && !!responder && client !== responder);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      name: name.trim(), description: description.trim(), ...(testCase || objectives.length ? { objectives } : {}),
      ...(withTraffic ? { traffic: { profile_id: mix, client_agent_id: client, responder_agent_id: responder } } : {}),
    };
    try {
      if (testCase) await api.put(`/campaign-cases/${testCase.id}`, body);
      else await api.post(`/campaigns/${campaignID}/cases`, body);
      onSaved();
    } catch (err) {
      setError(`Not saved: ${err instanceof Error ? err.message : String(err)}`);
      setBusy(false);
    }
  }

  return (
    <Modal title={testCase ? "Edit playbook" : "Add playbook"} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Name" value={name} onChange={setName} placeholder="Pull the primary WAN link" />
        <Field label="Description (optional)" value={description} onChange={setDescription}
          placeholder="What is done, and what should happen" />
        <ObjectiveEditor tests={tests} value={objectives} onChange={setObjectives} />
        <fieldset style={{ border: 0, padding: 0, margin: "14px 0 0" }}>
          <label style={{ fontWeight: 400 }}>
            <input type="checkbox" checked={withTraffic} disabled={agents.length < 2}
              onChange={(e) => setWithTraffic(e.target.checked)} />{" "}
            Run an application mix during each run
          </label>
          {agents.length < 2 ? (
            <div className="muted" style={{ fontSize: 12 }}>Needs two shards in the realm.</div>
          ) : withTraffic ? (
            mixes.length === 0 ? (
              <div className="banner warn" style={{ marginTop: 8 }}>
                No saved mixes yet. Save one under Configuration → Traffic profiles first.
              </div>
            ) : (
              <div className="field-grid" style={{ marginTop: 6 }}>
                <div>
                  <label htmlFor="case-mix">Mix</label>
                  <select id="case-mix" value={mix} onChange={(e) => setMix(e.target.value)}>
                    {mixes.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="case-client">From</label>
                  <select id="case-client" value={client} onChange={(e) => setClient(e.target.value)}>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="case-responder">To</label>
                  <select id="case-responder" value={responder} onChange={(e) => setResponder(e.target.value)}>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              </div>
            )
          ) : null}
          {withTraffic && client === responder ? <div className="err">Choose two different shards.</div> : null}
          {withTraffic ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Starts with the run and stops with it, both ways at once; the report shows what each application experienced.
            </div>
          ) : null}
        </fieldset>

            {error ? <div className="banner error" role="alert" style={{ marginTop: 14 }}>{error}</div> : null}
        <div className="row" style={{ marginTop: 18, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose}>Cancel</button>
          <button className="primary" type="submit" disabled={busy || !name.trim() || !trafficReady}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}
